// TASK 5 -- THE SCREEN'S HALF OF "REFUSE TO FINALISE A QUOTE THAT CANNOT PRICE".
//
// ⚠️ A COURTESY, NOT THE GUARANTEE. Save Quote finalises: it issues a
// permanent quote number, and there is no revert and no delete. The backend
// refuses a quote with a line that cannot price (anixify.quote.
// _check_can_finalise) BEFORE it draws that number, and that refusal is what
// actually protects the numbering -- an older bundle, or any other client,
// still meets it. This only disables the button first, so the person learns
// why before pressing rather than after.
//
// ⚠️ DERIVED FROM THE WIRE, NOT RESTATED. Whether an import line reaches the
// backend with a usable rate is decided by materialToBackendSaveLine: a set
// conversionRate is sent as it stands (0 included), an unset one is left out
// for the backend to resolve from the FX schedule -- which it can only do when
// the quote names a supplier currency, and otherwise stores 0. So this asks
// that function what it would send, rather than keeping a second copy of its
// rule that could drift from the first.
//
// ⚠️ CANNOT-PRICE, NOT ZERO. A rate of 0 converts every cost to Rs 0: missing
// input, never a price. A stated unit price of 0 is a free line and is NOT
// refused here or on the backend. A missing unit price is the unpriced guard's
// business (services/unpricedGuard.ts), which already stops the save.

import type { Material } from "@/types";
import { materialToBackendSaveLine } from "@/services/api";

export interface FinaliseBlocker {
  /** 1-based, in row order -- the same "Line N" the backend's refusal uses. */
  position: number;
  partNo: string;
  /** The rate the backend would store for this line. Never > 0 here. */
  rate: number;
}

/** Lines the backend would refuse to finalise. Empty means Save may proceed.
 *  Only an IMPORT quote converts through a rate; domestic never blocks. */
export function finaliseBlockers(
  rows: Material[],
  mode: "domestic" | "import" | undefined,
  supplierCurrency?: string,
): FinaliseBlocker[] {
  if (mode !== "import") return [];
  const blocked: FinaliseBlocker[] = [];
  rows.forEach((m, index) => {
    const wire = materialToBackendSaveLine(m, "import") as { conversion_rate?: number };
    let stored: number;
    if (wire.conversion_rate !== undefined) {
      stored = wire.conversion_rate;
    } else if (!supplierCurrency) {
      // Left out, and nothing can resolve it: the backend stores 0.
      stored = 0;
    } else {
      // Left out for the schedule to answer. A currency with no rate on file
      // fails the save on the backend with its own message; nothing to add.
      return;
    }
    if (!(stored > 0)) {
      blocked.push({ position: index + 1, partNo: m.partNo, rate: stored });
    }
  });
  return blocked;
}

/** The reason, in words, for the disabled button. Names every line and the
 *  missing input, and says what to do -- never just "cannot finalise". */
export function finaliseBlockedMessage(
  blocked: FinaliseBlocker[],
  supplierCurrency?: string,
): string {
  const lines = blocked
    .map((b) => `Line ${b.position} (${b.partNo || "no part number"})`)
    .join(", ");
  const fix = supplierCurrency
    ? `This quote names ${supplierCurrency}, but a rate stated on a line overrides `
      + `the FX schedule. The Conv. Rate column is editable while Supplier Currency `
      + `is "Not recorded": enter a rate greater than 0 there.`
    : `This import quote does not name its supplier currency, so nothing supplies `
      + `a rate. Enter a conversion rate greater than 0 in the Conv. Rate column.`;
  return `Save Quote issues a permanent quote number, so it is off until every `
    + `line can be priced. ${lines} ${blocked.length === 1 ? "has" : "have"} no `
    + `conversion rate, which prices a line at Rs 0. ${fix}`;
}
